/**
 * IDD (Input Data Dictionary) Lexer
 *
 * Tokenizes EnergyPlus IDD files into a stream of tokens for parsing.
 * IDD format is similar to IDF but includes backslash-prefixed properties
 * that define field metadata.
 */

import { type IDDToken, type IDDTokenType } from './types';

export class IDDLexer {
  private input: string;
  private position: number = 0;
  private line: number = 1;
  private column: number = 1;
  private tokens: IDDToken[] = [];

  constructor(input: string) {
    // Normalize line endings
    this.input = input.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  }

  /**
   * Tokenize the entire input
   */
  tokenize(): IDDToken[] {
    this.tokens = [];
    this.position = 0;
    this.line = 1;
    this.column = 1;

    while (this.position < this.input.length) {
      this.scanToken();
    }

    this.tokens.push({
      type: 'EOF',
      value: '',
      line: this.line,
      column: this.column,
    });

    return this.tokens;
  }

  private scanToken(): void {
    const char = this.current();

    // Newline
    if (char === '\n') {
      this.addToken('Newline', '\n');
      this.advance();
      this.line++;
      this.column = 1;
      return;
    }

    // Whitespace (excluding newlines)
    if (this.isWhitespace(char)) {
      this.scanWhitespace();
      return;
    }

    // Comment (! at start of line or after field)
    if (char === '!') {
      this.scanComment();
      return;
    }

    // Group header (\group)
    if (char === '\\') {
      this.scanProperty();
      return;
    }

    // Comma
    if (char === ',') {
      this.addToken('Comma', ',');
      this.advance();
      return;
    }

    // Semicolon
    if (char === ';') {
      this.addToken('Semicolon', ';');
      this.advance();
      return;
    }

    // Check if we're at the start of a line (after any whitespace/newlines)
    // and this could be a class name or field ID
    if (this.isClassNameStart()) {
      this.scanClassNameOrFieldId();
      return;
    }

    // Default: skip unknown character
    this.advance();
  }

  /**
   * Check if current position is at the start of a class name
   */
  private isClassNameStart(): boolean {
    const char = this.current();
    return /[A-Za-z]/.test(char);
  }

  /**
   * Scan a class name or field ID (A1, N1, etc.)
   */
  private scanClassNameOrFieldId(): void {
    const startLine = this.line;
    const startColumn = this.column;
    let value = '';

    // Read the identifier
    while (
      this.position < this.input.length &&
      !this.isDelimiter(this.current())
    ) {
      value += this.current();
      this.advance();
    }

    value = value.trim();

    // Determine if this is a field ID (A1, A2, N1, N2, etc.)
    if (/^[AN]\d+$/i.test(value)) {
      this.tokens.push({
        type: 'FieldId',
        value: value.toUpperCase(),
        line: startLine,
        column: startColumn,
      });
    } else if (value.length > 0) {
      this.tokens.push({
        type: 'ClassName',
        value,
        line: startLine,
        column: startColumn,
      });
    }
  }

  /**
   * Scan whitespace characters
   */
  private scanWhitespace(): void {
    const startLine = this.line;
    const startColumn = this.column;
    let value = '';

    while (
      this.position < this.input.length &&
      this.isWhitespace(this.current()) &&
      this.current() !== '\n'
    ) {
      value += this.current();
      this.advance();
    }

    if (value.length > 0) {
      this.tokens.push({
        type: 'Whitespace',
        value,
        line: startLine,
        column: startColumn,
      });
    }
  }

  /**
   * Scan a comment (starts with !)
   */
  private scanComment(): void {
    const startLine = this.line;
    const startColumn = this.column;
    let value = '';

    // Skip the !
    this.advance();
    value = '!';

    // Read until end of line
    while (this.position < this.input.length && this.current() !== '\n') {
      value += this.current();
      this.advance();
    }

    this.tokens.push({
      type: 'Comment',
      value,
      line: startLine,
      column: startColumn,
    });
  }

  /**
   * Scan a property (starts with \)
   */
  private scanProperty(): void {
    const startLine = this.line;
    const startColumn = this.column;

    // Skip the backslash
    this.advance();

    // Read the property name
    let propertyName = '';
    while (
      this.position < this.input.length &&
      !this.isWhitespace(this.current()) &&
      this.current() !== '\n' &&
      this.current() !== '\\' &&
      this.current() !== ',' &&
      this.current() !== ';'
    ) {
      propertyName += this.current();
      this.advance();
    }

    // Special case: \group is a group header
    if (propertyName.toLowerCase() === 'group') {
      // Read the rest of the line as the group name
      this.skipWhitespaceOnLine();
      let groupName = '';
      while (this.position < this.input.length && this.current() !== '\n') {
        groupName += this.current();
        this.advance();
      }
      this.tokens.push({
        type: 'GroupHeader',
        value: groupName.trim(),
        line: startLine,
        column: startColumn,
      });
      return;
    }

    // Add backslash token
    this.tokens.push({
      type: 'Backslash',
      value: '\\',
      line: startLine,
      column: startColumn,
    });

    // Add property name token
    if (propertyName.length > 0) {
      this.tokens.push({
        type: 'PropertyName',
        value: propertyName,
        line: startLine,
        column: startColumn + 1,
      });

      // Skip whitespace after property name
      this.skipWhitespaceOnLine();

      // Read property value (rest of line or until another backslash)
      let propertyValue = '';
      const valueStartColumn = this.column;
      while (
        this.position < this.input.length &&
        this.current() !== '\n' &&
        this.current() !== '\\'
      ) {
        propertyValue += this.current();
        this.advance();
      }

      propertyValue = propertyValue.trim();
      if (propertyValue.length > 0) {
        this.tokens.push({
          type: 'PropertyValue',
          value: propertyValue,
          line: startLine,
          column: valueStartColumn,
        });
      }
    }
  }

  /**
   * Skip whitespace on the current line (not including newlines)
   */
  private skipWhitespaceOnLine(): void {
    while (
      this.position < this.input.length &&
      this.isWhitespace(this.current()) &&
      this.current() !== '\n'
    ) {
      this.advance();
    }
  }

  /**
   * Get the current character
   */
  private current(): string {
    return this.input[this.position] || '';
  }

  /**
   * Advance to the next character
   */
  private advance(): void {
    this.position++;
    this.column++;
  }

  /**
   * Add a token to the list
   */
  private addToken(type: IDDTokenType, value: string): void {
    this.tokens.push({
      type,
      value,
      line: this.line,
      column: this.column,
    });
  }

  /**
   * Check if a character is whitespace (excluding newline)
   */
  private isWhitespace(char: string): boolean {
    return char === ' ' || char === '\t' || char === '\r';
  }

  /**
   * Check if a character is a delimiter
   */
  private isDelimiter(char: string): boolean {
    return (
      char === ',' ||
      char === ';' ||
      char === '\\' ||
      char === '!' ||
      char === '\n' ||
      char === ''
    );
  }
}
